import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuidv4 } from 'uuid';
import {
  OrganizationInsertDto,
  OrganizationExistenceCheckDto,
  BatchOperationResultDto,
  BatchItemResultDto,
} from '../api/types/dtos/organization.dto';
import { ORGANIZATION_FIELDS_METADATA } from '../config/userUploaderConfig';
import CsvFileUploader from '../components/UserUploader/CsvFileUploader';
import FieldMappingGrid, { FieldMappings } from '../components/UserUploader/FieldMappingGrid';
import CollisionCheckBanner from '../components/UserUploader/CollisionCheckBanner';
import PreviewTable from '../components/UserUploader/PreviewTable';
import SubmitBar from '../components/UserUploader/SubmitBar';
import UploadReport from '../components/UserUploader/UploadReport';
import OperationModeSelector, { OperationMode } from '../components/UserUploader/OperationModeSelector';
import CsvSummaryBanner from '../components/UserUploader/CsvSummaryBanner';
import ManualFarmForm from '../components/UserUploader/ManualFarmForm';
import ManualDeleteFarmForm from '../components/UserUploader/ManualDeleteFarmForm';
import { useOrganization } from '../contexts/OrganizationContext';
import { organizationService } from '../api/services/organization.service';
import { parseCoordinateColumn, joinMultiColumnPhones, joinMultiColumnGroups, parseCropsFromColumns } from '../utils/fieldTransforms';
import { downloadCsvTemplate } from '../utils/csvTemplate';
import { downloadOrgExport } from '../utils/orgExportFile';

type InputMode = 'csv' | 'manual';

interface CsvData {
  headers: string[];
  data: Record<string, string>[];
}

const BATCH_SIZE = 50;

const UserUploaderPage: React.FC = () => {
  const { selectedOrganization, loading: orgContextLoading } = useOrganization();
  const parentOrganizationId = selectedOrganization?.id;

  const [csvData, setCsvData] = useState<CsvData | null>(null);
  const [mappings, setMappings] = useState<FieldMappings>({});
  const [inputMode, setInputMode] = useState<InputMode>('csv');
  const [manualOp, setManualOp] = useState<OperationMode>('upsert');
  const [mode, setMode] = useState<OperationMode>('upsert');
  const [existingAction, setExistingAction] = useState<'skip' | 'update'>('skip');

  const [previewData, setPreviewData] = useState<OrganizationInsertDto[]>([]);
  const [previewGenerated, setPreviewGenerated] = useState(false);

  const [collisionCheck, setCollisionCheck] = useState<OrganizationExistenceCheckDto | null>(null);
  const [collisionLoading, setCollisionLoading] = useState(false);
  const [collisionError, setCollisionError] = useState<string | null>(null);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<BatchOperationResultDto | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);

  const [csvParsingError, setCsvParsingError] = useState<string | null>(null);

  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleCsvParsed = useCallback((data: CsvData) => {
    setCsvData(data);
    setCsvParsingError(null);
    setMappings({});
    setPreviewData([]);
    setPreviewGenerated(false);
    setCollisionCheck(null);
    setSubmitResult(null);
    setSubmitError(null);
  }, []);

  const handleCsvParsingError = useCallback((error: string) => {
    setCsvParsingError(error);
    setCsvData(null);
    setMappings({});
    setPreviewData([]);
    setPreviewGenerated(false);
  }, []);

  const handleExport = useCallback(async (format: 'csv' | 'json') => {
    if (!parentOrganizationId) return;
    setExportLoading(true);
    setExportError(null);
    try {
      const orgs = await organizationService.exportOrganizations(parentOrganizationId);
      downloadOrgExport(orgs, format);
    } catch (err: any) {
      setExportError(err?.message || 'Error al descargar las organizaciones.');
    } finally {
      setExportLoading(false);
    }
  }, [parentOrganizationId]);

  const handleMappingsChange = useCallback((newMappings: FieldMappings) => {
    setMappings(newMappings);
    setPreviewGenerated(false);
    setCollisionCheck(null);
  }, []);

  const handleModeChange = useCallback((newMode: OperationMode) => {
    setMode(newMode);
    setPreviewGenerated(false);
    setCollisionCheck(null);
    setSubmitResult(null);
  }, []);

  const handleInputModeChange = useCallback((newMode: InputMode) => {
    setInputMode(newMode);
    setManualOp('upsert');
    setCsvData(null);
    setMappings({});
    setPreviewData([]);
    setPreviewGenerated(false);
    setCollisionCheck(null);
    setSubmitResult(null);
    setSubmitError(null);
    setBatchProgress(null);
    setCsvParsingError(null);
    setExportError(null);
  }, []);

  const handleReset = useCallback(() => {
    setCsvData(null);
    setMappings({});
    setPreviewData([]);
    setPreviewGenerated(false);
    setCollisionCheck(null);
    setSubmitResult(null);
    setSubmitError(null);
    setBatchProgress(null);
    setExportError(null);
  }, []);

  const trimStringValues = useCallback((obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === 'string') return obj.trim();
    if (Array.isArray(obj)) {
      return obj.map((item) => (typeof item === 'string' ? item.trim() : trimStringValues(item)));
    }
    if (typeof obj === 'object') {
      const trimmed: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          trimmed[key] = trimStringValues(obj[key]);
        }
      }
      return trimmed;
    }
    return obj;
  }, []);

  const generatePreviewData = useCallback((): OrganizationInsertDto[] => {
    if (!csvData) return [];

    return csvData.data.map((row, index) => {
      const clientInsertId = uuidv4();
      const orgDto: Partial<OrganizationInsertDto> = { insertId: clientInsertId };

      for (const fieldMeta of ORGANIZATION_FIELDS_METADATA) {
        const key = fieldMeta.key as string;
        const mapping = mappings[key];

        if (fieldMeta.hidden && fieldMeta.defaultValue !== undefined) {
          (orgDto as any)[key] = fieldMeta.defaultValue;
          continue;
        }

        if (!mapping || !mapping.enabled) {
          if (fieldMeta.defaultValue !== undefined) {
            (orgDto as any)[key] = fieldMeta.defaultValue;
          }
          continue;
        }

        let csvValue: any = undefined;

        if (fieldMeta.parser === 'multi_column' && mapping.csvColumns && mapping.csvColumns.length > 0) {
          if (key === 'ownerPhoneNumbers') {
            csvValue = joinMultiColumnPhones(row, mapping.csvColumns, fieldMeta.stringArraySeparator);
          } else if (key === 'groupIds') {
            const groups = joinMultiColumnGroups(row, mapping.csvColumns);
            csvValue = groups.length > 0 ? groups : undefined;
          }
        } else if (fieldMeta.parser === 'crops' && mapping.csvColumns && mapping.csvColumns.length > 0) {
          csvValue = parseCropsFromColumns(row, mapping.csvColumns);
        } else if (fieldMeta.parser === 'coordinate_split' && mapping.csvColumn) {
          const rawVal = row[mapping.csvColumn];
          if (rawVal && rawVal.trim() !== '') {
            const parsed = parseCoordinateColumn(rawVal);
            if (key === 'latitude') {
              (orgDto as any)[key] = parsed.lat;
              continue;
            } else if (key === 'longitude') {
              (orgDto as any)[key] = parsed.long;
              continue;
            }
          }
        } else if (mapping.csvColumn && row[mapping.csvColumn] !== undefined) {
          csvValue = row[mapping.csvColumn];
        }

        if (typeof csvValue === 'string') {
          csvValue = csvValue.trim();
        }

        const isEmpty =
          csvValue === undefined ||
          csvValue === '' ||
          (Array.isArray(csvValue) && csvValue.length === 0) ||
          (typeof csvValue === 'object' && csvValue !== null && Object.keys(csvValue).length === 0);

        if (isEmpty) {
          if (mapping.customValue !== undefined && mapping.customValue !== null && mapping.customValue !== '') {
            csvValue = mapping.customValue;
          } else if (fieldMeta.defaultValue !== undefined) {
            csvValue = fieldMeta.defaultValue;
          } else {
            continue;
          }
        }

        if (csvValue !== undefined && csvValue !== null) {
          switch (fieldMeta.type) {
            case 'number':
              (orgDto as any)[key] = csvValue === '' ? null : parseFloat(String(csvValue)) || null;
              break;
            case 'boolean':
              if (typeof csvValue === 'boolean') {
                (orgDto as any)[key] = csvValue;
              } else {
                (orgDto as any)[key] = String(csvValue).toLowerCase() === 'true' || String(csvValue) === '1';
              }
              break;
            case 'string[]':
              if (Array.isArray(csvValue)) {
                (orgDto as any)[key] = csvValue.map((s) => String(s).trim()).filter((s) => s !== '');
              } else if (typeof csvValue === 'string' && fieldMeta.stringArraySeparator) {
                (orgDto as any)[key] = csvValue.split(fieldMeta.stringArraySeparator).map((s) => s.trim()).filter((s) => s !== '');
              }
              break;
            case 'object_map_string_number':
              if (typeof csvValue === 'object' && Object.keys(csvValue).length > 0) {
                (orgDto as any)[key] = csvValue;
              }
              break;
            default:
              (orgDto as any)[key] = String(csvValue);
          }
        }
      }

      const trimmedDto = trimStringValues(orgDto) as Partial<OrganizationInsertDto>;

      if (!trimmedDto.name || trimmedDto.name.trim() === '') {
        trimmedDto.name = `Preview Org ${index + 1}`;
      }
      if (!trimmedDto.idTypeId) trimmedDto.idTypeId = 'CC';
      if (!trimmedDto.idNumber) trimmedDto.idNumber = `PREVIEW${index}`;
      if (!trimmedDto.ownerName) trimmedDto.ownerName = `Preview Owner ${index + 1}`;
      if (!trimmedDto.ownerIdTypeId) trimmedDto.ownerIdTypeId = 'CC';
      if (!trimmedDto.ownerIdNumber) trimmedDto.ownerIdNumber = `PREVIEW_OWN${index}`;

      return trimmedDto as OrganizationInsertDto;
    });
  }, [csvData, mappings, trimStringValues]);

  const duplicateIdNumbers = useMemo(() => {
    if (!previewData.length) return new Set<string>();
    const seen = new Map<string, number>();
    for (const row of previewData) {
      if (row.idNumber) {
        seen.set(row.idNumber, (seen.get(row.idNumber) || 0) + 1);
      }
    }
    const dupes = new Set<string>();
    for (const [id, count] of seen) {
      if (count > 1) dupes.add(id);
    }
    return dupes;
  }, [previewData]);

  const handleGeneratePreview = useCallback(async () => {
    if (!csvData || !parentOrganizationId) return;

    const data = generatePreviewData();
    setPreviewData(data);
    setPreviewGenerated(true);

    const idNumbers = data.map((d) => d.idNumber).filter((n): n is string => !!n);
    if (idNumbers.length === 0) return;

    setCollisionLoading(true);
    setCollisionError(null);

    try {
      const result = await organizationService.checkExistingOrganizations(
        parentOrganizationId,
        { idNumbers }
      );
      setCollisionCheck(result);
    } catch (err: any) {
      setCollisionError(err?.message || 'Error al verificar organizaciones existentes');
      setCollisionCheck(null);
    } finally {
      setCollisionLoading(false);
    }
  }, [csvData, parentOrganizationId, generatePreviewData]);

  const existingCount = collisionCheck ? Object.keys(collisionCheck.existingOrgs).length : 0;
  const newCount = collisionCheck ? collisionCheck.notFoundIdNumbers.length : 0;

  const handleSubmit = useCallback(async () => {
    if (!previewData.length || !parentOrganizationId) {
      setSubmitError('Faltan datos de vista previa o organizacion padre.');
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitResult(null);

    const trimmedData = previewData.map((org) => trimStringValues(org));

    try {
      if (mode === 'delete') {
        const idNumbers = trimmedData.map((d) => d.idNumber).filter((n): n is string => !!n);
        const chunks: string[][] = [];
        for (let i = 0; i < idNumbers.length; i += BATCH_SIZE) {
          chunks.push(idNumbers.slice(i, i + BATCH_SIZE));
        }

        let created: BatchItemResultDto[] = [];
        let skipped: BatchItemResultDto[] = [];
        let failed: BatchItemResultDto[] = [];

        for (let i = 0; i < chunks.length; i++) {
          setBatchProgress({ current: i + 1, total: chunks.length });
          try {
            const result = await organizationService.batchDeleteOrganizations(
              parentOrganizationId,
              { idNumbers: chunks[i] }
            );
            created = [...created, ...result.created];
            skipped = [...skipped, ...result.skipped];
            failed = [...failed, ...result.failed];
          } catch (err: any) {
            console.error(`Delete batch ${i + 1} failed:`, err);
            failed = [...failed, ...chunks[i].map((id) => ({
              name: null,
              idNumber: id,
              success: false,
              errorMessage: `Batch failed: ${err?.message || 'Unknown error'}`,
            } as BatchItemResultDto))];;
          }
        }

        setSubmitResult({ created, updated: [], skipped, failed });
      } else {
        const chunks: OrganizationInsertDto[][] = [];
        for (let i = 0; i < trimmedData.length; i += BATCH_SIZE) {
          chunks.push(trimmedData.slice(i, i + BATCH_SIZE));
        }

        let allCreated: BatchItemResultDto[] = [];
        let allUpdated: BatchItemResultDto[] = [];
        let allSkipped: BatchItemResultDto[] = [];
        let allFailed: BatchItemResultDto[] = [];

        for (let i = 0; i < chunks.length; i++) {
          setBatchProgress({ current: i + 1, total: chunks.length });
          try {
            const result = await organizationService.batchUpsertOrganizations(
              parentOrganizationId,
              { organizations: chunks[i], updateIfExists: existingAction === 'update' }
            );
            allCreated = [...allCreated, ...result.created];
            allUpdated = [...allUpdated, ...result.updated];
            allSkipped = [...allSkipped, ...result.skipped];
            allFailed = [...allFailed, ...result.failed];
          } catch (err: any) {
            console.error(`Upsert batch ${i + 1} failed:`, err);
            allFailed = [...allFailed, ...chunks[i].map((org) => ({
              name: org.name,
              idNumber: org.idNumber,
              success: false,
              errorMessage: `Batch failed: ${err?.message || 'Unknown error'}`,
            } as BatchItemResultDto))];
          }
        }

        setSubmitResult({
          created: allCreated,
          updated: allUpdated,
          skipped: allSkipped,
          failed: allFailed,
        });
      }
    } catch (err: any) {
      setSubmitError(err?.message || 'Ocurrio un error durante el envio.');
    } finally {
      setBatchProgress(null);
      setSubmitLoading(false);
    }
  }, [previewData, parentOrganizationId, mode, existingAction, trimStringValues]);

  const showMapping = csvData !== null && !submitResult;
  const showReport = submitResult !== null;

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ my: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1">
            Gestor de Lotes de Organizaciones
          </Typography>
          {csvData && inputMode === 'csv' && (
            <OperationModeSelector mode={mode} onChange={handleModeChange} disabled={submitLoading} />
          )}
        </Box>

        {orgContextLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!parentOrganizationId && !orgContextLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No hay organizacion padre seleccionada. Por favor selecciona una desde el encabezado.
          </Alert>
        )}

        {csvParsingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {csvParsingError}
          </Alert>
        )}

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {!csvData && !orgContextLoading && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ToggleButtonGroup
                value={inputMode}
                exclusive
                size="small"
                onChange={(_, val) => {
                  if (val !== null) handleInputModeChange(val as InputMode);
                }}
              >
                <ToggleButton value="csv">
                  <UploadFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Subir CSV
                </ToggleButton>
                <ToggleButton value="manual">
                  <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Crear manualmente
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {inputMode === 'csv' ? (
              <Box>
                <CsvFileUploader onCsvParsed={handleCsvParsed} onParsingError={handleCsvParsingError} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={downloadCsvTemplate}
                  >
                    Descargar plantilla CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    disabled={exportLoading || !parentOrganizationId}
                    onClick={() => handleExport('csv')}
                  >
                    Descargar CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    disabled={exportLoading || !parentOrganizationId}
                    onClick={() => handleExport('json')}
                  >
                    Descargar JSON
                  </Button>
                </Box>
              </Box>
            ) : (
              parentOrganizationId && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <OperationModeSelector
                      mode={manualOp}
                      onChange={setManualOp}
                    />
                  </Box>
                  {manualOp === 'delete' ? (
                    <ManualDeleteFarmForm parentOrganizationId={parentOrganizationId} />
                  ) : (
                    <ManualFarmForm parentOrganizationId={parentOrganizationId} />
                  )}
                </Box>
              )
            )}
          </Box>
        )}

        {showMapping && parentOrganizationId && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
              Asignar campos a columnas CSV
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Los campos se asignan automaticamente a las columnas CSV. Revisa y ajusta segun sea necesario. Los valores por defecto se usan cuando las celdas CSV estan vacias.
            </Typography>

            <FieldMappingGrid
              csvHeaders={csvData!.headers}
              csvSampleData={csvData!.data.slice(0, 5)}
              mappings={mappings}
              onMappingsChange={handleMappingsChange}
              isDeleteMode={mode === 'delete'}
              existingAction={existingAction}
              onExistingActionChange={setExistingAction}
            />

            <CsvSummaryBanner headers={csvData!.headers} data={csvData!.data} />

            {previewGenerated && (
              <>
                <Divider sx={{ my: 2 }} />
                <CollisionCheckBanner
                  checkResult={collisionCheck}
                  loading={collisionLoading}
                  error={collisionError}
                  duplicateCount={duplicateIdNumbers.size}
                  totalCount={previewData.length}
                  mode={mode}
                  existingAction={existingAction}
                />
                <PreviewTable
                  data={previewData}
                  existingOrgs={collisionCheck?.existingOrgs}
                  duplicateIds={duplicateIdNumbers}
                  mode={mode}
                  onDataUpdate={setPreviewData}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={submitLoading}
                  >
                    Subir nuevo archivo
                  </Button>
                  <SubmitBar
                    mode={mode}
                    totalCount={previewData.length}
                    newCount={newCount}
                    existingCount={existingCount}
                    existingAction={existingAction}
                    onSubmit={handleSubmit}
                    onReset={handleReset}
                    loading={submitLoading}
                    batchProgress={batchProgress}
                    disabled={!parentOrganizationId}
                  />
                </Box>
              </>
            )}

            {!previewGenerated && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={submitLoading}
                >
                  Subir nuevo archivo
                </Button>
                <SubmitBar
                  mode={mode}
                  totalCount={csvData?.data.length ?? 0}
                  newCount={0}
                  existingCount={0}
                  existingAction={existingAction}
                  onSubmit={handleGeneratePreview}
                  onReset={handleReset}
                  loading={false}
                  batchProgress={null}
                  disabled={!csvData || !parentOrganizationId}
                  isPreviewAction
                />
              </Box>
            )}
          </Box>
        )}

        {showReport && submitResult && (
          <Box>
            <UploadReport
              reportData={submitResult.created}
              updatedData={submitResult.updated}
              skippedData={submitResult.skipped}
              failedData={submitResult.failed}
              mode={mode}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <SubmitBar
                mode={mode}
                totalCount={0}
                newCount={0}
                existingCount={0}
                existingAction={existingAction}
                onSubmit={() => {}}
                onReset={handleReset}
                loading={false}
                batchProgress={null}
                disabled={true}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default UserUploaderPage;
